import { Modify } from 'ol/interaction'
import useMap from 'hooks/use-map'
import { useState } from 'react'
import { boundaryChanged } from 'actions/map'
import {
  toGeoJson,
  convertGeoJsonToOpenLayersFeature,
  unKink,
  to180Degrees,
} from 'common/ol/utils'
import MultiPolygon from 'ol/geom/MultiPolygon'
import { get } from 'es-toolkit/compat'
import { saveFeatures } from 'api/field'

import Point from 'ol/geom/Point'
import Feature from 'ol/Feature'
import { platformModifierKeyOnly } from 'ol/events/condition'
import difference from '@turf/difference'
import union from '@turf/union'

function deletePoint(pixel, featuresArray, map) {
  if (!pixel) return
  let f = []
  try {
    f = map.getFeaturesAtPixel(pixel, {
      hitTolerance: 5,
    })
  } catch (err) {
    console.error('modify-interaction:deletePoint-1', err)
  }
  let coordinate
  for (let i = 0; i < f.length; i++) {
    const geom = f[i].getGeometry()
    if (geom instanceof Point) {
      coordinate = geom.getCoordinates()
    }
  }
  if (coordinate) {
    let start = 0
    let p = []
    for (let i = 0; i < featuresArray.length; i++) {
      const geometry = featuresArray[i].getGeometry()
      const vertex = geometry.getClosestPoint(coordinate)
      const coordinates = geometry.flatCoordinates
      const endArray = geometry.endss_
      for (let e = 0; e < endArray.length; e++) {
        let newPolygon = []
        const ends = endArray[e]
        for (let j = 0; j < ends.length; j++) {
          try {
            const polygon = coordinates.slice(start, ends[j])
            const indexOne = polygon.indexOf(vertex[0])
            const indexTwo = polygon.indexOf(vertex[1])
            let endEl = []
            let ring = []
            for (let k = 0; k < polygon.length; k += 2) {
              if (k === indexOne && polygon.length === 8) {
                if (indexOne === 0) {
                  //This is the first and last point we are removing
                  const nextPoint = k + 2
                  if (nextPoint + 1 < polygon.length) {
                    endEl.push(polygon[nextPoint])
                    endEl.push(polygon[nextPoint + 1])
                  }
                }
              } else {
                ring.push([polygon[k], polygon[k + 1]])
              }
            }
            if (endEl.length && ring.length) {
              ring[ring.length - 1] = endEl
            }
            if (indexOne > -1 && indexTwo > -1 && indexOne + 1 === indexTwo) {
              if (polygon.length > 8) {
                newPolygon.push(ring)
              }
            } else {
              newPolygon.push(ring)
            }
          } catch (error) {
            console.error('modify-interaction:deletePoint-1', error)
          }
          if (j === 0 && newPolygon.length === 0) {
            start = ends[ends.length - 1]
            break
          } else {
            start = ends[j]
          }
        }
        if (newPolygon.length) {
          p.push(newPolygon)
        }
      }
      let _feature
      let fixed
      try {
        _feature = new Feature({
          geometry: new MultiPolygon(p),
        })
        fixed = fixFeature(_feature)
      } catch (err) {
        console.error('modify-interaction:deletePoint-2', err)
      }
      if (fixed) {
        try {
          const olFeature = convertGeoJsonToOpenLayersFeature(fixed)
          featuresArray[i].setGeometry(olFeature.getGeometry())
        } catch (err) {
          console.error('modify-interaction:deletePoint-3', err)
        }
      }
    }
  }
}

function fixFeature(feature) {
  const currentFeature = toGeoJson(feature)
  let featureCollection = {
    type: 'FeatureCollection',
    features: [],
  }
  let holes = {
    type: 'Feature',
    geometry: {
      type: 'MultiPolygon',
      coordinates: [],
    },
  }
  for (let i = 0; i < currentFeature.features.length; i++) {
    try {
      let polygons = {
        type: 'Feature',
        geometry: {
          type: 'MultiPolygon',
          coordinates: [],
        },
      }
      const feature = currentFeature.features[i]

      const geometryType = feature.geometry.type
      const coordinates =
        geometryType === 'MultiPolygon'
          ? feature.geometry.coordinates
          : [feature.geometry.coordinates]
      for (let j = 0; j < coordinates.length; j++) {
        polygons.geometry.coordinates.push([coordinates[j][0]])
        for (let k = 1; k < coordinates[j].length; k++) {
          holes.geometry.coordinates.push([coordinates[j][k]])
        }
        featureCollection.features.push(polygons)
        if (j > 0) {
          featureCollection.features = [union(featureCollection)]
        }
      }
    } catch (err) {
      console.error('modify-interaction:fixFeatures', err)
    }
  }
  //featureCollection.features.push(polygons)
  featureCollection.features.push(holes)
  try {
    if (featureCollection.features.length > 1) {
      const res = difference(featureCollection)
      if (res.geometry.type === 'Polygon') {
        res.geometry.type = 'MultiPolygon'
        res.geometry.coordinates = [res.geometry.coordinates]
      }
      res.id = feature.id
      res.properties = feature.properties
      currentFeature.features = [res]
    } else {
      currentFeature.features = featureCollection.features
    }
  } catch (err) {
    console.error('modify-interaction:fixFeatures', err)
  }

  const unkinked = unKink(currentFeature, false, false)
  return unkinked
}

export default function ModifyInteraction({ features }) {
  const map = useMap()

  const dispatch = useDispatch()
  const [modified, setModified] = useState(false)
  const [interaction, setInteraction] = useState()
  const field = useSelector((state) => get(state, 'field.singleton'))

  useEffect(() => {
    if (features) {
      const featuresArray = features.getArray()
      for (let i = 0; i < featuresArray.length; i++) {
        const geometry = toGeoJson(featuresArray[i])
        let updated = false
        for (let j = 0; j < geometry.features.length; j++) {
          const res = to180Degrees(geometry.features[j])
          if (res.updated) {
            updated = res.updated
            geometry.features[j].coordinates = res.coordinates
          }
        }
        if (updated) {
          let coords = []
          dispatch(saveFeatures(field.id, geometry, false))
          if (geometry.features.length > 1) {
            for (let i = 0; i < geometry.features.length; i++) {
              const workingCoords = geometry.features[i].geometry.coordinates
              coords.push(workingCoords)
            }
          } else {
            coords = geometry.features[0].geometry.coordinates
          }
          const geom = new MultiPolygon(coords).transform(
            'EPSG:4326',
            'EPSG:3857',
          )
          featuresArray[i].setGeometry(geom)
        }
      }
      const interaction = new Modify({
        features,
        deleteCondition: platformModifierKeyOnly,
      })

      map.addInteraction(interaction)
      setInteraction(interaction)

      return function () {
        map.removeInteraction(interaction)
      }
    } else {
      console.log('no features found')
    }
  }, [map, features])

  useEffect(() => {
    if (!interaction) return

    function onEditStart(e) {
      if (!modified) {
        setModified(true)
        dispatch(boundaryChanged())
      }
    }
    interaction.on('modifystart', onEditStart)
    return () => {
      interaction.un('modifystart', onEditStart)
    }
  }, [interaction])

  useEffect(() => {
    function onEdit(e) {
      const isDelete = platformModifierKeyOnly(e.mapBrowserEvent)
      const pixel = e?.target?.lastPointerEvent_?.pixel
      if (isDelete) {
        e.preventDefault()
        e.stopPropagation()
        const featuresToModify = e?.features.getArray()
        deletePoint(pixel, featuresToModify, map)
      } else {
        const featuresArray = e.features.getArray()
        for (let i = 0; i < featuresArray.length; i++) {
          const fixedFeature = fixFeature(featuresArray[i])
          featuresArray[i].setGeometry(
            convertGeoJsonToOpenLayersFeature(fixedFeature).getGeometry(),
          )
        }
      }
    }
    if (!interaction) return
    interaction.on('modifyend', onEdit)
    return () => {
      interaction.un('modifyend', onEdit)
    }
  }, [interaction, features, map])
  return null
}
